import React from "react";
import { Alert, Chip, Stack, Typography } from "@mui/material";
import SelectableCopyList from "../components/SelectableCopyList";
import { useSourceCmsPages } from "../hooks/useSourceEntities";
import { useAlreadyCopied, useCopyCmsPages } from "../hooks/useCopyLog";
import type { CopyOperationResult } from "../types";

interface Props {
  destPortalId: string;
  sourcePortalId: string;
  onResult: (r: CopyOperationResult) => void;
}

const CmsPagesImportPanel: React.FC<Props> = ({
  destPortalId,
  sourcePortalId,
  onResult,
}) => {
  const { data: pages = [], isLoading: loadingList } =
    useSourceCmsPages(sourcePortalId || null);
  const { data: copied, isLoading: loadingCopied } = useAlreadyCopied(
    destPortalId,
    sourcePortalId || null,
    "cms_page",
  );
  const copyMutation = useCopyCmsPages();

  if (!sourcePortalId) {
    return <Alert severity="info">Select a source portal above to see its CMS pages.</Alert>;
  }

  // Login page IDs — always selectable for re-sync
  const loginPageIds = new Set(pages.filter((p) => p.slug === "login").map((p) => p.id));
  const filteredCopiedIds = (copied?.source_entity_ids ?? []).filter((id) => !loginPageIds.has(id));

  const rows = pages.map((p) => {
    const depth = p.depth ?? 0;
    return {
      id: p.id,
      primary: (
        <span style={{ paddingLeft: depth * 24 }}>
          {depth > 0 && <span style={{ color: "#9e9e9e", marginRight: 4 }}>└</span>}
          {p.title}
        </span>
      ),
      secondary: `/${p.slug}`,
      meta: p.slug === "login" ? (
        <Chip label="Login page" size="small" color="info" variant="outlined" />
      ) : p.is_public ? (
        <Chip label="Public" size="small" color="success" variant="outlined" />
      ) : (
        <Chip label="Restricted" size="small" color="warning" variant="outlined" />
      ),
    };
  });

  const handleCopy = async (ids: string[]) => {
    const items = ids.map((id) => {
      const page = pages.find((p) => p.id === id)!;
      return { id: page.id, name: page.title };
    });
    const result = await copyMutation.mutateAsync({
      destPortalId,
      sourcePortalId,
      items,
    });
    onResult(result);
  };

  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        Pages are copied with content, layout, and branding — but{" "}
        <strong>agent embeds are cleared</strong>. Pages that were{" "}
        <strong>restricted</strong> in the source stay restricted (hidden from
        end-users) until you assign roles in this portal. Public pages remain
        public. Slug conflicts are auto-renamed.
      </Alert>
      <Typography variant="caption" color="text.secondary">
        Showing {pages.length} page{pages.length === 1 ? "" : "s"} from the source portal.
      </Typography>
      <SelectableCopyList
        rows={rows}
        alreadyCopiedIds={filteredCopiedIds}
        loading={loadingList || loadingCopied}
        emptyMessage="No CMS pages in the source portal."
        onCopy={handleCopy}
        isCopying={copyMutation.isPending}
        ctaLabel="Copy selected pages"
      />
    </Stack>
  );
};

export default CmsPagesImportPanel;
